import { useTranslation } from "react-i18next";
import { MyAreaTitle } from "../components";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { Field, FieldArray, Form, Formik, useFormikContext } from "formik";
import Button from "../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";
import { useMutation, useQuery } from "react-query";
import {
  fetchCareerOptions,
  updateUserDetails,
} from "../../../../../services/onboarding.service";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import {
  getYearsOfExperienceRanges,
  JobHistory,
  UserDetails,
} from "../../../onboarding/types";
import de from "date-fns/locale/de";
import enUS from "date-fns/locale/en-US";
import { getLocaleFromLanguage } from "../../../../../services/content.service";
import {
  ContentAndProgressContext,
  useContentAndProgress,
} from "../../../../../contexts/UserProgressContext";
import CloseIcon from "@mui/icons-material/Close";
import ArrowLeft from "../../../../../assets/arrows-left.svg";
import ArrowRight from "../../../../../assets/arrows-right.svg";
const AddJobButton = ({
  push,
}: {
  push: (obj: Partial<JobHistory>) => void;
}) => {
  const { values, setFieldValue } = useFormikContext<{
    currentJobTitle?: string;
    currentExperience?: string;
    jobHistories?: JobHistory[];
  }>();
  const { t } = useTranslation("onboarding");
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );

  const handleAddClick = () => {
    const title = values.currentJobTitle?.trim();
    const experience = values.currentExperience;

    if (title && experience) {
      push({
        lastPosition: title,
        experienceInYears: experience,
      });
      setFieldValue("currentJobTitle", "");
      setFieldValue("currentExperience", "");
      setValidationMessage(null);
    } else {
      setValidationMessage(
        t(
          "stepBlueCareer.addJobValidationError",
          "Please add the title and years"
        )
      );
    }
  };

  return (
    <div className="mt-4 flex justify-end items-center space-x-4">
      {validationMessage && (
        <span className="text-sm text-red-600 ">{validationMessage}</span>
      )}
      <Button
        type="button"
        variant={ButtonVariant.SECONDARY}
        size={ButtonSize.SMALL}
        onClick={handleAddClick}
      >
        {t("stepBlueCareer.addJob", "+ Add Job Entry")}
      </Button>
    </div>
  );
};

const DirtyChecker = ({
  initialValues,
  setIsFormDirty,
}: {
  initialValues: any;
  setIsFormDirty: (isDirty: boolean) => void;
}) => {
  const { values } = useFormikContext();

  useEffect(() => {
    if (JSON.stringify(values) !== JSON.stringify(initialValues)) {
      setIsFormDirty(true);
    } else {
    }
  }, [values, initialValues, setIsFormDirty]);

  return null;
};

export function CareerInformation() {
  const { user, setUser, configurations } = useContext(AuthContext);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { t } = useTranslation("myArea");
  const { t: onboardingT } = useTranslation("onboarding");
  const { t: unlockT } = useTranslation("unlock");

  const [industries, setIndustries] = useState([]);
  const [careerLevels, setCareerLevels] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [salary, setSalary] = useState([]);
  const { phases, milestones } = useContentAndProgress();

  const { updateUserProgressTree } = useContentAndProgress();

  const [initialJobType, setInitialJobType] = useState(
    user?.userDetails?.newJobType || ""
  );
  const newJobTypes = [
    { key: "employment", value: "employment" },
    { key: "freelance", value: "freelance" },
    { key: "startup", value: "startup" },
    { key: "employmentCareerChange", value: "employmentCareerChange" },
    { key: "other", value: "other" },
  ];

  const shouldRenderCareerInformation =
    configurations?.includesCareerInformation;
  const shouldRenderCareerExpectations =
    configurations?.includesCareerExpectations;
  const shouldRenderCareerPreferences =
    configurations?.includesCareerPreferences;
  const productType = configurations?.productType;
  const shouldRenderNewJobPreferences = user?.userDetails?.newJobType;

  const availableTabs = useMemo(() => {
    const tabs = [];
    // Tab 1: Career Info (includes history for BLUE)
    if (shouldRenderCareerInformation) {
      tabs.push({
        id: "careerInfo",
        labelKey: "career-information.tabs.careerInfo",
      });
    }
    // Tab 2: Expectations
    if (shouldRenderCareerExpectations) {
      tabs.push({
        id: "expectations",
        labelKey: "career-information.tabs.expectations",
      });
    }
    // Tab 3: Preferences / Job Search
    if (shouldRenderCareerPreferences && productType !== "BLUE") {
      tabs.push({
        id: "preferences",
        labelKey: "career-information.tabs.preferences",
      });
    }
    // Tab 4: New Job Info (Unlock Phase 4)
    if (shouldRenderNewJobPreferences) {
      tabs.push({ id: "newJob", labelKey: "career-information.tabs.newJob" });
    }
    return tabs;
  }, [
    shouldRenderCareerInformation,
    shouldRenderCareerExpectations,
    shouldRenderCareerPreferences,
    shouldRenderNewJobPreferences,
  ]);

  const [activeTab, setActiveTab] = useState<string>(
    availableTabs[0]?.id || ""
  );

  const tabContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollByOneTab = () => {
    const container = tabContainerRef.current;
    if (!container) return 100;
    const visibleWidth = container.offsetWidth;
    return visibleWidth / 3;
  };

  const updateArrows = () => {
    const container = tabContainerRef.current;
    if (!container) return;
    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft + container.offsetWidth < container.scrollWidth
    );
  };

  const scroll = (direction: "left" | "right") => {
    const container = tabContainerRef.current;
    if (!container) return;
    const scrollAmount = scrollByOneTab();
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  // Update arrow states on scroll and resize
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;

    updateArrows();
    container.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);

    return () => {
      container.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [availableTabs.length]);

  // Update active tab if the available tabs change and the current active one disappears
  useEffect(() => {
    if (
      availableTabs.length > 0 &&
      !availableTabs.some((tab) => tab.id === activeTab)
    ) {
      setActiveTab(availableTabs[0].id);
    } else if (availableTabs.length === 0) {
      setActiveTab("");
    }
  }, [availableTabs, activeTab]);

  useQuery(["getCareerOptions"], fetchCareerOptions, {
    onSuccess: (data) => {
      setIndustries(data?.industries);
      setCareerLevels(data?.careerLevels);
      setDisciplines(data?.disciplines);
      setSalary(data?.salary);
    },
  });

  const handleSubmit = async (values: typeof initialValues) => {
    const payloadForBackend: any = {
      lastPosition: values.lastPosition?.trim() || null,
      experienceInYears:
        productType === "BLUE" ? values.experienceInYears || null : null,
      careerLevel: productType !== "BLUE" ? values.careerLevel || null : null,
      industry: values.industry || null,
      discipline: productType !== "BLUE" ? values.discipline || null : null,

      jobHistories: [],

      jobSearchCity: values.jobSearchCity?.trim() || null,
      timingLastDayAtWork: values.timingLastDayAtWork,
      timingAchieveRestart: values.timingAchieveRestart,
      desiredPosition: values.desiredPosition?.trim() || null,
      desiredCareerLevel: values.desiredCareerLevel || null,
      desiredDiscipline: values.desiredDiscipline || null,
      desiredIndustry: values.desiredIndustry || null,
      desiredSalary: values.desiredSalary || null,
      newJobType: values.newJobType || null,
      newJobStartDate: values.newJobStartDate || null,
      newJobTitle: values.newJobTitle?.trim() || null,
      newJobComments: values.newJobComments?.trim() || null,
    };

    if (productType === "BLUE") {
      const finalJobsHistoryForPayload: Partial<JobHistory>[] = [];

      const currentJobDataFromForm: Partial<JobHistory> = {
        lastPosition: values.lastPosition?.trim() || null,
        experienceInYears: values.experienceInYears || null,
        isCurrent: true,

        id: user?.userDetails?.jobHistories?.find((h) => h.isCurrent)?.id,
      };
      if (
        currentJobDataFromForm.lastPosition ||
        currentJobDataFromForm.experienceInYears
      ) {
        finalJobsHistoryForPayload.push(currentJobDataFromForm);
      }

      const previousJobsFromForm = (values.jobHistories || [])
        .map((job) => ({
          id: job.id,
          lastPosition: job.lastPosition?.trim() || null,
          experienceInYears: job.experienceInYears || null,
          isCurrent: false,
        }))
        .filter(
          (job) => job.lastPosition !== null || job.experienceInYears !== null
        );

      payloadForBackend.jobHistories =
        finalJobsHistoryForPayload.concat(previousJobsFromForm);

      delete payloadForBackend.careerLevel;
      delete payloadForBackend.discipline;
    } else {
      delete payloadForBackend.experienceInYears;
      delete payloadForBackend.jobHistories;
    }

    try {
      await submitNewUserDetails.mutate({
        userDetails: payloadForBackend as UserDetails,
      });
    } catch (error) {
      console.error("ERROR submitting user details update:", error);
    }
  };

  const submitNewUserDetails = useMutation(updateUserDetails, {
    onSuccess: (response) => {
      setUser(response);
      setIsFormDirty(false);

      setTimeout(() => {
        if (user?.userDetails?.newJobType) {
          updateUserProgressTree();
        }
      }, 2000);
    },
    onError: (error: any) => {
      console.log("Error: ", error);
    },
  });

  const initialValues = useMemo(() => {
    const userDetailsData: any | null | undefined = user?.userDetails;
    const allUserJobHistories: JobHistory[] =
      userDetailsData?.jobHistories || [];

    const currentJobHistory = allUserJobHistories.find((h) => h.isCurrent);

    const previousJobHistories = allUserJobHistories.filter(
      (h) => !h.isCurrent
    );

    return {
      lastPosition: userDetailsData?.lastPosition || "",
      experienceInYears:
        productType === "BLUE"
          ? currentJobHistory?.experienceInYears || ""
          : "",
      careerLevel:
        productType !== "BLUE" ? userDetailsData?.careerLevel || "" : "",
      industry: userDetailsData?.industry || "",
      discipline:
        productType !== "BLUE" ? userDetailsData?.discipline || "" : "",

      jobHistories: previousJobHistories.map((h) => ({
        id: h.id,
        lastPosition: h.lastPosition || "",
        experienceInYears: h.experienceInYears || "",
      })),

      currentJobTitle: "",
      currentExperience: "",

      jobSearchCity: userDetailsData?.jobSearchCity || "",
      timingLastDayAtWork: userDetailsData?.timingLastDayAtWork
        ? new Date(userDetailsData.timingLastDayAtWork)
        : null,
      timingAchieveRestart: userDetailsData?.timingAchieveRestart
        ? new Date(userDetailsData.timingAchieveRestart)
        : null,
      desiredPosition: userDetailsData?.desiredPosition || "",
      desiredCareerLevel: userDetailsData?.desiredCareerLevel || "",
      desiredDiscipline: userDetailsData?.desiredDiscipline || "",
      desiredIndustry: userDetailsData?.desiredIndustry || "",
      desiredSalary: userDetailsData?.desiredSalary || "",
      newJobType: userDetailsData?.newJobType || "",
      newJobStartDate: userDetailsData?.newJobStartDate || "",
      newJobTitle: userDetailsData?.newJobTitle || "",
      newJobComments: userDetailsData?.newJobComments || "",
    };
  }, [user, productType]);

  const monthsLocal = getLocaleFromLanguage();
  const localeMapping = {
    de: de,
    "en-US": enUS,
  };

  const convertedLocale = localeMapping[monthsLocal];

  // useEffect(() => {
  //   if (user?.userDetails?.newJobType !== initialJobType) {
  //     setInitialJobType(user?.userDetails?.newJobType || "");
  //   }
  // }, [user?.userDetails?.newJobType]);

  // useEffect(() => {
  //   // Only proceed if newJobType is defined and there are phases
  //   if (user?.userDetails?.newJobType && phases && phases?.length > 0) {
  //     updateUserProgressTree()
  //     const lastPhase = phases?.[phases.length - 1]?.content;
  //     const lastPhaseId = lastPhase?.platformContentId;
  //     const lastPhaseMilestones = lastPhase?.childContentIds;

  //     // Function to find the first relevant milestone
  //     const findFirstRelevantMilestone = () => {
  //       for (const childContentId of lastPhaseMilestones) {
  //         const milestone = milestones?.find((m) => m?.content?.platformContentId === childContentId);

  //         if (milestone && !milestone?.markedIrrelevant) { // Check if markedIrrelevant is false
  //           return milestone;
  //         }
  //       }
  //       return null;
  //     };

  //     const firstRelevantMilestone = findFirstRelevantMilestone();
  //     const milestoneId = firstRelevantMilestone?.content?.platformContentId;
  //     console.log(lastPhaseId,milestoneId)

  //     if (lastPhaseId && milestoneId) {
  //       localStorage.setItem(
  //         `lastState-${user?.id}`,
  //         JSON.stringify({
  //           phase: lastPhaseId,
  //           milestone: milestoneId,
  //         })
  //       );
  //     }
  //   }
  // }, [user?.userDetails?.newJobType, phases, milestones]);

  return (
    <div className="mb-5">
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("career-information.title")} />
      </div>
      <div className="mb-4 ">
        <span>{t(`career-information.explanatory-text`)}</span>
      </div>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <DirtyChecker
                initialValues={initialValues}
                setIsFormDirty={setIsFormDirty}
              />

              {availableTabs.length > 0 && (
                <>
                  <div className=" hidden md:block w-full border-b border-gray-300 mb-6  whitespace-nowrap">
                    <div className="flex ">
                      {availableTabs.map((tab) => (
                        <div
                          key={tab.id}
                          className={`px-4 py-2 cursor-pointer relative shrink-0  ${
                            activeTab === tab.id
                              ? "font-bold text-gray-800"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                          onClick={() => setActiveTab(tab.id)}
                          role="tab"
                          aria-selected={activeTab === tab.id}
                        >
                          <span>{t(tab.labelKey)}</span>{" "}
                          {activeTab === tab.id && (
                            <div className="absolute bottom-0 left-0 h-[2px] bg-Yellow-default w-full" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="md:hidden w-full mb-6">
                    <div className="relative border-b border-gray-300">
                      {/* Arrows positioned absolutely on the sides */}
                      {availableTabs.length > 3 && (
                        <button
                          onClick={() => scroll("left")}
                          disabled={!canScrollLeft}
                          className="absolute left-[-12px] top-0 bottom-0 z-10  bg-white disabled:opacity-30"
                        >
                          <img
                            src={ArrowLeft}
                            alt="Previous"
                            className="w-[12px] h-[12px]"
                          />
                        </button>
                      )}
                      <div
                        ref={tabContainerRef}
                        className="flex overflow-x-auto whitespace-nowrap mx-1"
                      >
                        {availableTabs.map((tab) => (
                          <div
                            key={tab.id}
                            className={`px-3 py-2 cursor-pointer relative shrink-0 ${
                              activeTab === tab.id
                                ? "font-bold text-gray-800"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                            onClick={() => setActiveTab(tab.id)}
                            role="tab"
                            aria-selected={activeTab === tab.id}
                          >
                            <span>{t(tab.labelKey)}</span>
                            {activeTab === tab.id && (
                              <div className="absolute bottom-0 left-0 h-[2px] bg-Yellow-default w-full" />
                            )}
                          </div>
                        ))}
                      </div>
                      {availableTabs.length > 3 && (
                        <button
                          onClick={() => scroll("right")}
                          disabled={!canScrollRight}
                          className="absolute right-[-12px] top-0 bottom-0 z-10  bg-white disabled:opacity-30"
                          aria-label="Scroll right"
                        >
                          <img
                            src={ArrowRight}
                            alt="Next"
                            className="w-[12px] h-[12px]"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeTab === "careerInfo" && shouldRenderCareerInformation && (
                <>
                  <h2 className="text-Grey-dark font-bold text-lg mb-2">
                    {t("career-information.career")}
                  </h2>
                  <div className="mb-4">
                    {t("career-information.career-description")}
                  </div>
                  <div className="flex flex-col w-full">
                    <label className="text-base mb-1">
                      {onboardingT("stepCareer.lastPosition")}
                    </label>
                    <Field
                      as="input"
                      type="text"
                      id="last-position"
                      name="lastPosition"
                      placeholder={onboardingT(
                        "stepCareer.lastPositionPlaceHolder"
                      )}
                      className="border rounded-lg px-4  text-base mb-4"
                    />

                    {productType === "BLUE" ? (
                      // --- BLUE Product Type Fields ---
                      <>
                        <div>
                          <label
                            htmlFor="experienceInYears"
                            className="text-base mb-1 block"
                          >
                            {onboardingT("stepBlueCareer.experienceInYears")}
                          </label>
                          <Field
                            as="select"
                            id="experienceInYears"
                            name="experienceInYears"
                            className="border rounded-lg px-4 text-base w-full mb-4"
                          >
                            <option value="">
                              {onboardingT("stepBlueCareer.please-select")}
                            </option>
                            {getYearsOfExperienceRanges(t).map((range) => (
                              <option key={range.key} value={range.value}>
                                {range.label}
                              </option>
                            ))}
                          </Field>
                        </div>
                      </>
                    ) : (
                      <>
                        <label className="text-base mb-1">
                          {onboardingT("stepCareer.careerLevel")}
                        </label>

                        <Field
                          as="select"
                          id="career-select"
                          name="careerLevel"
                          className="border rounded-lg  px-4   text-base mb-4"
                        >
                          <option value="" disabled>
                            {onboardingT("stepCareer.please-select")}
                          </option>
                          {careerLevels?.map((careerLevel: any) => (
                            <option
                              key={careerLevel?.key}
                              value={careerLevel?.value}
                            >
                              {onboardingT(`careerLevel.${careerLevel?.value}`)}
                            </option>
                          ))}
                        </Field>

                        <label className="text-base mb-1">
                          {onboardingT("stepCareer.discipline")}
                        </label>
                        <Field
                          as="select"
                          id="discipline-select"
                          name="discipline"
                          className="border rounded-lg  px-4   text-base mb-6"
                          value={values.discipline}
                        >
                          <option value="" disabled>
                            {onboardingT("stepCareer.please-select")}
                          </option>
                          {disciplines?.map((discipline: any) => (
                            <option
                              key={discipline?.key}
                              value={discipline?.value}
                            >
                              {onboardingT(`disciplines.${discipline?.value}`)}
                            </option>
                          ))}
                        </Field>
                      </>
                    )}
                    <label className="text-base mb-1">
                      {onboardingT("stepCareer.industry")}
                    </label>

                    <Field
                      as="select"
                      id="industry-select"
                      name="industry"
                      className="border rounded-lg px-4   text-base  "
                      value={values.industry}
                    >
                      <option value="" disabled>
                        {onboardingT("stepCareer.please-select")}
                      </option>
                      {industries?.map((industry: any) => (
                        <option key={industry?.key} value={industry?.value}>
                          {onboardingT(`industries.${industry?.value}`)}
                        </option>
                      ))}
                    </Field>
                  </div>
                  {productType === "BLUE" && (
                    <div className=" pt-6 ">
                      {" "}
                      {/* Separator */}
                      <h2 className="text-Grey-dark font-bold text-lg mb-4">
                        {onboardingT(
                          "stepBlueCareer.previousJob",
                          "Previous Jobs"
                        )}
                      </h2>
                      <FieldArray name="jobHistories">
                        {({ remove, push }) => (
                          <div className="space-y-4">
                            {" "}
                            {/* Outer container */}
                            {/* 1. Display Area for ADDED jobs */}
                            {values.jobHistories &&
                              values.jobHistories.length > 0 && (
                                <div className="space-y-3 pb-4">
                                  {" "}
                                  {/* List of added jobs */}
                                  {values.jobHistories.map((job, index) => {
                                    // if (index === 0) return null;
                                    return (
                                      <div
                                        key={job.id || `new-${index}`} // Use ID if available for stable key
                                        className="flex items-center justify-between px-4 py-2 bg-gray-100 border  rounded-md"
                                      >
                                        <div className="">
                                          <span className="">
                                            {job.lastPosition || "N/A"}
                                          </span>
                                          <span className=" ml-2">
                                            (
                                            {getYearsOfExperienceRanges(t).find(
                                              (r) =>
                                                r.value ===
                                                job.experienceInYears
                                            )?.label || "N/A"}
                                            )
                                          </span>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => remove(index)}
                                          className="ml-4 text-gray-500 hover:text-red-600"
                                        >
                                          <CloseIcon fontSize="small" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            <div className="space-y-4 border rounded-md p-4 mt-4">
                              {" "}
                              <h3 className="text-base font-semibold -mt-1 mb-3">
                                {onboardingT(
                                  "stepBlueCareer.addNewJobTitle",
                                  "Add New Previous Job Entry"
                                )}
                              </h3>
                              {/* Input Job Title */}
                              <div>
                                <label
                                  htmlFor="currentJobTitle"
                                  className="block  mb-1 "
                                >
                                  {onboardingT(
                                    "stepBlueCareer.job-title",
                                    "Job Title / Position"
                                  )}
                                </label>
                                <Field
                                  id="currentJobTitle"
                                  name="currentJobTitle"
                                  placeholder={onboardingT(
                                    "stepBlueCareer.jobTitlePlaceholder"
                                  )}
                                  type="text"
                                  className="border rounded-lg w-full px-4  text-base  "
                                />
                              </div>
                              {/* Input Experience */}
                              <div>
                                <label
                                  htmlFor="currentExperience"
                                  className="block  mb-1 "
                                >
                                  {onboardingT(
                                    "stepBlueCareer.experienceInYears",
                                    "Years of Experience"
                                  )}
                                </label>
                                <Field
                                  as="select"
                                  id="currentExperience"
                                  name="currentExperience"
                                  className="border rounded-lg w-full px-4  text-base  "
                                >
                                  <option value="">
                                    {onboardingT(
                                      "stepBlueCareer.please-select"
                                    )}
                                  </option>
                                  {getYearsOfExperienceRanges(t).map(
                                    (range) => (
                                      <option
                                        key={range.key}
                                        value={range.value}
                                      >
                                        {range.label}
                                      </option>
                                    )
                                  )}
                                </Field>
                              </div>
                              {/* Add Button Component */}
                              <AddJobButton push={push} />
                            </div>
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  )}{" "}
                </>
              )}
              {activeTab === "expectations" &&
                shouldRenderCareerExpectations && (
                  <>
                    <div className="mb-4">
                      {t("career-information.career-expectations-description")}
                    </div>
                    <div>
                      <div className="flex flex-col w-full">
                        <label htmlFor="date-picker" className="text-base mb-1">
                          {onboardingT("stepNeeds.lastDay")}
                        </label>
                        <DatePicker
                          id="date-picker"
                          selected={values?.timingLastDayAtWork}
                          onChange={(date: any) => {
                            setFieldValue("timingLastDayAtWork", date);
                            setIsFormDirty(true);
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText={
                            t("select-a-date") || "Select a date"
                          }
                          className="border rounded-lg px-4  text-base w-full mb-4"
                          locale={convertedLocale}
                        />
                        <label htmlFor="date-picker" className="text-base mb-1">
                          {onboardingT("stepNeeds.timeToRestart")}
                        </label>
                        <DatePicker
                          id="date-picker"
                          selected={values?.timingAchieveRestart}
                          onChange={(date: any) => {
                            setFieldValue("timingAchieveRestart", date);
                            setIsFormDirty(true);
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText={
                            t("select-a-date") || "Select a date"
                          }
                          className="border rounded-lg px-4  text-base w-full mb-6"
                          locale={convertedLocale}
                        />
                      </div>
                    </div>
                  </>
                )}

              {activeTab === "preferences" && shouldRenderCareerPreferences && (
                <>
                  <div className="mb-4">
                    {t("career-information.job-search-description")}
                  </div>
                  <div>
                    <div className="flex flex-col w-full">
                      <label className="text-base mb-1">
                        {onboardingT("stepPreferences.desiredPosition")}
                      </label>
                      <Field
                        as="input"
                        type="text"
                        id="desired-position"
                        name="desiredPosition"
                        placeholder={onboardingT(
                          "stepPreferences.desiredPositionPlaceHolder"
                        )}
                        className="border rounded-lg px-4  text-base mb-4"
                        value={values.desiredPosition}
                      />

                      <label className="text-base mb-1">
                        {onboardingT("stepPreferences.desiredCareerLevel")}
                      </label>

                      <Field
                        as="select"
                        id="desiredCareer-select"
                        name="desiredCareerLevel"
                        className="border rounded-lg  px-4   text-base mb-4"
                        value={values.desiredCareerLevel}
                      >
                        <option value="" disabled>
                          {onboardingT("stepCareer.please-select")}
                        </option>
                        {careerLevels?.map((careerLevel: any) => (
                          <option
                            key={careerLevel?.key}
                            value={careerLevel?.value}
                          >
                            {onboardingT(`careerLevel.${careerLevel?.value}`)}
                          </option>
                        ))}
                      </Field>

                      <label className="text-base mb-1">
                        {onboardingT("stepPreferences.desiredIndustry")}
                      </label>

                      <Field
                        as="select"
                        id="desiredIndustry-select"
                        name="desiredIndustry"
                        className="border rounded-lg px-4   text-base  mb-4"
                        value={values.desiredIndustry}
                      >
                        <option value="" disabled>
                          {onboardingT("stepCareer.please-select")}
                        </option>
                        {industries?.map((industry: any) => (
                          <option key={industry?.key} value={industry?.value}>
                            {onboardingT(`industries.${industry?.value}`)}
                          </option>
                        ))}
                      </Field>

                      <label className="text-base mb-1">
                        {onboardingT("stepPreferences.desiredDiscipline")}
                      </label>
                      <Field
                        as="select"
                        id="desiredDiscipline-select"
                        name="desiredDiscipline"
                        className="border rounded-lg  px-4   text-base mb-4"
                        value={values.desiredDiscipline}
                      >
                        <option value="" disabled>
                          {onboardingT("stepCareer.please-select")}
                        </option>
                        {disciplines?.map((discipline: any) => (
                          <option
                            key={discipline?.key}
                            value={discipline?.value}
                          >
                            {onboardingT(`disciplines.${discipline?.value}`)}
                          </option>
                        ))}
                      </Field>

                      <label className="text-base mb-1">
                        {onboardingT("stepPreferences.desiredSalary")}
                      </label>
                      <Field
                        as="select"
                        id="desiredSalary-select"
                        name="desiredSalary"
                        className="border rounded-lg  px-4   text-base mb-4"
                        value={values.desiredSalary}
                      >
                        <option value="" disabled>
                          {onboardingT("stepCareer.please-select")}
                        </option>
                        {salary?.map((salary: any) => (
                          <option key={salary?.key} value={salary?.value}>
                            {onboardingT(`salary.${salary?.value}`)}
                          </option>
                        ))}
                      </Field>

                      <label className="text-base mb-1">
                        {onboardingT("stepName.jobSearchCity")}
                      </label>
                      <Field
                        as="input"
                        type="text"
                        id="jobSearchCity"
                        name="jobSearchCity"
                        placeholder={onboardingT(
                          "stepName.jobSearchCityPlaceHolder"
                        )}
                        className="border rounded-lg px-4 text-base mb-6"
                        value={values.jobSearchCity}
                      />
                    </div>
                  </div>
                </>
              )}

              {activeTab === "newJob" && shouldRenderNewJobPreferences && (
                <>
                  <div className="mb-4">{t("newJob.description")}</div>
                  <div>
                    <div className="flex flex-col w-full">
                      <label className="text-base mb-1">
                        {t("newJob.act-as")}
                      </label>

                      <Field
                        as="select"
                        name="newJobType"
                        className="border rounded-lg px-4  text-base mb-4"
                        value={values.newJobType}
                      >
                        <option value="" disabled>
                          {unlockT("ill-act-as-placeholder")}
                        </option>
                        {newJobTypes.map((jobType) => (
                          <option key={jobType.key} value={jobType.value}>
                            {unlockT(`newJobType.${jobType.value}`)}
                          </option>
                        ))}
                      </Field>

                      <label className="text-base mb-1">
                        {t("newJob.on-this-day")}
                      </label>
                      <DatePicker
                        selected={
                          values.newJobStartDate
                            ? new Date(values.newJobStartDate)
                            : null
                        }
                        onChange={(date: any) => {
                          setFieldValue(
                            "newJobStartDate",
                            date?.toISOString().substr(0, 10)
                          );
                          setIsFormDirty(true);
                        }}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={
                          t("newJob.on-this-day-placeholder") || "Select a date"
                        }
                        className="w-full px-4 mt-1 border-black rounded-lg mb-4"
                      />

                      <label className="text-base mb-1">
                        {t("newJob.new-title")}
                      </label>

                      <Field
                        as="input"
                        type="text"
                        name="newJobTitle"
                        placeholder={t("newJob.new-title-placeholder")}
                        className="border rounded-lg px-4  text-base mb-4"
                        value={values.newJobTitle}
                      />

                      <label className="text-base mb-1">
                        {t("newJob.comments")}
                      </label>

                      <Field
                        as="textarea"
                        name="newJobComments"
                        placeholder={t("newJob.comments-placeholder")}
                        className="border rounded-lg px-4  text-base"
                        value={values.newJobComments}
                        rows={4}
                      />
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                className={`mt-6 w-full ${isFormDirty ? "" : "bg-gray-200"}`}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.MEDIUM}
                disabled={!isFormDirty}
              >
                {t("profile.save")}
              </Button>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
